import { Global, css } from '@emotion/react'
import { Api } from '@pharmac-extranet/extranet-api'
import {
  FlexboxGrid,
  PharmacErrorToast,
  PharmacExtranetLightTheme,
  usePharmacToast,
} from '@pharmac/ui'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './app.css'
import { ShoppingCartProvider } from './components/navigation/shopping-cart/ShoppingCartProvider'
import { __EXTRANET_WRAPPER_WIDTH_PIXELS } from './constants'
import { AuthenticationProvider } from './features/authentication/AuthenticationProvider'
import { CustomerProvider } from './features/customer/CustomerProvider'
import { ExtranetRouterProvider } from './router/ExtranetRouter'

export const App = () => {
  const { add } = usePharmacToast()
  const {
    t,
    i18n: { language },
  } = useTranslation()

  useEffect(() => {
    document.title = t('pageTitle')
  }, [language])

  useEffect(() => {
    const api = new Api({ baseURL: import.meta.env['VITE_API_URL'] })

    const frontendVersion = import.meta.env['VITE_BUILDNUMBER']

    api.systemVersion.getSystemVersion().then((response) => {
      if (response && response.message) {
        if (language === 'en') {
          console.log(
            `%cThis is a browser feature intended for developers. Normal usage of OmaPro Portaali does not require the usage of this feature.`,
            'color: red; font-size: 15px'
          )
        }

        if (language === 'fi') {
          console.log(
            `%cTämä on selainominaisuus, joka on tarkoitettu kehittäjille. OmaPro Portaalin normaali käyttö ei vaadi tämän ominaisuuden käyttöä.`,
            'color: red; font-size: 15px'
          )
        }

        if (language === 'sv') {
          console.log(
            `%cDetta är en webbläsarfunktion avsedd för utvecklare. Normal användning av OmaPro Portalen kräver inte användning av denna funktion.`,
            'color: red; font-size: 15px'
          )
        }
        console.log(`%c${t('pageTitle')}`, 'color: #88288f; font-size: 35px;')
        console.log(`%cUI: ${frontendVersion}`, 'color: #00b1b9')
        console.log(`%cBE: ${response.message}`, 'color: #00b1b9')
      }
    })
  }, [])

  const [queryClient] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 10000,
          retry: 2,
        },
      },
      queryCache: new QueryCache({
        onError: (error: any) => {
          if (error.response?.status === 408) {
            return
          }

          if (error.response?.status === 500) {
            console.error(error.response.data)
          }
        },
      }),
      mutationCache: new MutationCache({
        onError: (error: any, _variables, _context, mutation) => {
          if (mutation.options.onError) {
            return
          }
          add({
            toast: <PharmacErrorToast>{error.message}</PharmacErrorToast>,
            autoClose: false,
          })
        },
      }),
    })
  )

  const tempExtranetThemeVariables = css`
    * {
      --extranet-wrapper-width: ${__EXTRANET_WRAPPER_WIDTH_PIXELS}px;
      --extranet-light-purple: #f3e9f4;
      --extranet-card-border-radius: 13px;
      --extranet-menu-bg-gradient: linear-gradient(
        180deg,
        rgba(243, 233, 244, 1) 0%,
        rgba(248, 243, 249, 1) 30%,
        #ffffff 100%
      );

      strong {
        font-weight: 600;
      }

      @supports (scrollbar-width: auto) {
        .enscrollbar {
          scrollbar-color: var(--color-primary) var(--color-white);
          scrollbar-width: thin;
        }
      }

      @supports selector(::-webkit-scrollbar) {
        .enscrollbar::-webkit-scrollbar-thumb {
          background: var(--color-primary);
        }
        .enscrollbar::-webkit-scrollbar-track {
          background: var(--color-white);
        }
        .enscrollbar::-webkit-scrollbar {
          max-width: 10px;
          max-height: 10px;
        }
      }
    }
  `

  return (
    <>
      <Global styles={tempExtranetThemeVariables} />
      <Global styles={PharmacExtranetLightTheme} />
      <Global styles={FlexboxGrid} />
      <QueryClientProvider client={queryClient}>
        <AuthenticationProvider>
          <React.StrictMode>
            <CustomerProvider>
              <ShoppingCartProvider>
                <ExtranetRouterProvider />
              </ShoppingCartProvider>
            </CustomerProvider>
          </React.StrictMode>
        </AuthenticationProvider>
      </QueryClientProvider>
    </>
  )
}
